// Per-conference, per-year tournament bracket schedules used by the
// TournamentBracket component to overlay real dates/times on the
// projected matchups. Lookups are by conference slug (underscore form,
// matching seed/conferences.yaml) + the fall-season year — so the
// 2026 SEC Soccer Tournament lives under SCHEDULES.sec[2026].
//
// Why a static file: conference tournaments publish one bracket per
// year per sport. The data volume is small (~1 dozen conferences × a
// handful of games each) and the refresh cadence is annual. A seed +
// ingest + endpoint pipeline would be idiomatic for the repo but
// over-weight for this payload; a TS constant hits the frontend
// without any backend involvement and is easy to update each August
// when conferences publish their schedules.
//
// How to add a conference: look up the conference's published bracket
// schedule, then map every game to a "<round>-<slot>" key. The round
// and slot indexes are the ones TournamentBracket uses — see
// build4/build6/build8/build10/build12 in tournament.ts for the exact
// layouts. The 12-team bracket's First Round is slots 1..4
// (8v9, 5v12, 6v11, 7v10); Quarterfinals is slots 1..4 (1v?, 4v?,
// 3v?, 2v?); Semifinals is 1..2; Final is 1.

import type { BracketSchedule } from "./tournament";

const SCHEDULES: Record<string, Record<number, BracketSchedule>> = {
  // SEC Women's Soccer Championship 2026, held at Ashton Brosnaham
  // Soccer Complex in Pensacola, FL (Nov 8-15, 2026). Game times
  // sourced from pensacolasports.org/docs/CST_Time_2026_SEC_Soccer_Champ_Schedule.pdf;
  // the PDF lists times in order but does not pin which specific
  // matchup is in each slot, so slot assignment is round-order
  // (slot 1 = earliest game, slot 4 = latest).
  sec: {
    2026: {
      rounds: {
        1: "Sun Nov 8",
        2: "Tue Nov 10",
        3: "Thu Nov 12",
        4: "Sun Nov 15",
      },
      games: {
        "1-1": { date: "Sun Nov 8", time: "12:30 PM ET" },
        "1-2": { date: "Sun Nov 8", time: "3:00 PM ET" },
        "1-3": { date: "Sun Nov 8", time: "5:30 PM ET" },
        "1-4": { date: "Sun Nov 8", time: "8:00 PM ET" },
        "2-1": { date: "Tue Nov 10", time: "12:30 PM ET" },
        "2-2": { date: "Tue Nov 10", time: "3:00 PM ET" },
        "2-3": { date: "Tue Nov 10", time: "5:30 PM ET" },
        "2-4": { date: "Tue Nov 10", time: "8:00 PM ET" },
        "3-1": { date: "Thu Nov 12", time: "4:30 PM ET" },
        "3-2": { date: "Thu Nov 12", time: "7:00 PM ET" },
        "4-1": { date: "Sun Nov 15", time: "2:30 PM ET" },
      },
    },
  },

  // Big Ten Women's Soccer Tournament 2026 — 8 teams, at Energizer
  // Park, St. Louis. QF Nov 4-5 with known times; SF Nov 8 and Final
  // Nov 13 at highest remaining seeds with no published tipoff time.
  // Source: bigten.org/news/2026/10/31 + bigten.org/sports/.../2026WSOCB1GTournament.
  // Slot assignment matches build8's seed layout: slot 1=1v8,
  // slot 2=4v5, slot 3=3v6, slot 4=2v7.
  big_ten: {
    2026: {
      rounds: {
        1: "Nov 4-5",
        2: "Sun Nov 8",
        3: "Fri Nov 13",
      },
      games: {
        "1-1": { date: "Wed Nov 4", time: "5:00 PM ET" },
        "1-2": { date: "Thu Nov 5", time: "5:00 PM ET" },
        "1-3": { date: "Thu Nov 5", time: "8:00 PM ET" },
        "1-4": { date: "Wed Nov 4", time: "8:00 PM ET" },
      },
    },
  },

  // Big 12 Women's Soccer Tournament 2026 — 8 teams, at Texas Health
  // Mansfield Stadium, Mansfield, TX. All QF + SF times published;
  // Championship Nov 14 has no listed time.
  // Source: en.wikipedia.org/wiki/2026_Big_12_Conference_women's_soccer_tournament.
  // Winner feeds: M1 (4v5, slot 2) + M2 (1v8, slot 1) → SF slot 1;
  // M3 (2v7, slot 4) + M4 (3v6, slot 3) → SF slot 2.
  big_12: {
    2026: {
      rounds: {
        1: "Mon Nov 9",
        2: "Wed Nov 11",
        3: "Sat Nov 14",
      },
      games: {
        "1-1": { date: "Mon Nov 9", time: "3:00 PM ET" }, // #1 vs #8
        "1-2": { date: "Mon Nov 9", time: "12:30 PM ET" }, // #4 vs #5
        "1-3": { date: "Mon Nov 9", time: "9:00 PM ET" }, // #3 vs #6
        "1-4": { date: "Mon Nov 9", time: "6:30 PM ET" }, // #2 vs #7
        "2-1": { date: "Wed Nov 11", time: "5:30 PM ET" }, // w(1)+w(2)
        "2-2": { date: "Wed Nov 11", time: "8:00 PM ET" }, // w(4)+w(3)
        "3-1": { date: "Sat Nov 14" },
      },
    },
  },

  // Big East Women's Soccer Championship 2026 — 4 teams, at Maryland
  // SportsPlex, Boyds, MD. SF Nov 11 at 5 PM / 7:30 PM ET; Final
  // Nov 14 at 7 PM ET. Which semifinal is at which time wasn't
  // published; slot 1 (1v4) by convention gets the earlier window.
  // Source: bigeast.com/news/2026/10/14.
  big_east: {
    2026: {
      rounds: {
        1: "Wed Nov 11",
        2: "Sat Nov 14",
      },
      games: {
        "1-1": { date: "Wed Nov 11", time: "5:00 PM ET" }, // 1v4
        "1-2": { date: "Wed Nov 11", time: "7:30 PM ET" }, // 2v3
        "2-1": { date: "Sat Nov 14", time: "7:00 PM ET" },
      },
    },
  },

  // Sun Belt Women's Soccer Championship 2026 — 8 teams, at Foley
  // Sports Tourism Complex, Foley, AL. All times published.
  // Source: sunbeltsports.org/news/2026/11/2.
  // Match-to-slot mapping: M1(3v6)→slot 3, M2(2v7)→slot 4,
  // M3(4v5)→slot 2, M4(1v8)→slot 1. SF M5(w(M1)+w(M2))=bottom-half
  // → SF slot 2; M6(w(M3)+w(M4))=top-half → SF slot 1.
  sun_belt: {
    2026: {
      rounds: {
        1: "Mon Nov 9",
        2: "Wed Nov 11",
        3: "Sat Nov 14",
      },
      games: {
        "1-1": { date: "Mon Nov 9", time: "8:00 PM ET" }, // #1 vs #8
        "1-2": { date: "Mon Nov 9", time: "5:30 PM ET" }, // #4 vs #5
        "1-3": { date: "Mon Nov 9", time: "12:00 PM ET" }, // #3 vs #6
        "1-4": { date: "Mon Nov 9", time: "2:30 PM ET" }, // #2 vs #7
        "2-1": { date: "Wed Nov 11", time: "8:00 PM ET" }, // top-half
        "2-2": { date: "Wed Nov 11", time: "5:00 PM ET" }, // bottom-half
        "3-1": { date: "Sat Nov 14", time: "8:00 PM ET" },
      },
    },
  },

  // American Conference (AAC) Women's Soccer Championship 2026 —
  // 8 teams, at Premier Sports Campus, Lakewood Ranch, FL. All
  // times published.
  // Source: theamerican.org/news/2026/11/4.
  // QF1(3v6)→slot 3, QF2(2v7)→slot 4, QF3(4v5)→slot 2, QF4(1v8)→slot 1.
  american: {
    2026: {
      rounds: {
        1: "Sun Nov 8",
        2: "Wed Nov 11",
        3: "Sat Nov 14",
      },
      games: {
        "1-1": { date: "Sun Nov 8", time: "7:30 PM ET" }, // #1 vs #8
        "1-2": { date: "Sun Nov 8", time: "5:00 PM ET" }, // #4 vs #5
        "1-3": { date: "Sun Nov 8", time: "11:00 AM ET" }, // #3 vs #6
        "1-4": { date: "Sun Nov 8", time: "1:30 PM ET" }, // #2 vs #7
        "2-1": { date: "Wed Nov 11", time: "7:00 PM ET" }, // top-half (SF2)
        "2-2": { date: "Wed Nov 11", time: "4:00 PM ET" }, // bottom-half (SF1)
        "3-1": { date: "Sat Nov 14", time: "7:00 PM ET" },
      },
    },
  },

  // Conference USA Women's Soccer Championship 2026 — 8 teams, host
  // Delaware. Round dates published; per-game times not posted in a
  // consolidated table so games[] stays empty except for FIU vs NMSU
  // (#4 vs #5) which the host school announced at 7 PM.
  // Source: fiusports.com/news/2026/11/2.
  cusa: {
    2026: {
      rounds: {
        1: "Sun Nov 8",
        2: "Tue Nov 10",
        3: "Fri Nov 13",
      },
      games: {
        "1-2": { date: "Sun Nov 8", time: "7:00 PM ET" }, // #4 vs #5
      },
    },
  },

  // ACC Women's Soccer Championship 2026 — 6 teams (confirmed by
  // seed/conferences.yaml). First Round Nov 8 on campus sites (no
  // central times). Semifinals Nov 12 at 11 AM and 2 PM ET; Final
  // Nov 15 at noon ET. Both semis are at First Horizon Stadium
  // (WakeMed Soccer Park, Cary, NC).
  // Source: theacc.com news feed Oct 28 + Sep 30.
  // build6 slots: R1 slot 1=4v5, slot 2=3v6. R2 slot 1=1 v w(1),
  // slot 2=2 v w(2). Convention assigns earlier SF time to slot 1.
  acc: {
    2026: {
      rounds: {
        1: "Sun Nov 8",
        2: "Thu Nov 12",
        3: "Sat Nov 15",
      },
      games: {
        "2-1": { date: "Thu Nov 12", time: "11:00 AM ET" },
        "2-2": { date: "Thu Nov 12", time: "2:00 PM ET" },
        "3-1": { date: "Sat Nov 15", time: "12:00 PM ET" },
      },
    },
  },

  // Patriot League Women's Soccer Championship 2026 — 6 teams. All
  // games at higher-seed campus sites; the league published dates
  // only, with every kickoff listed as TBA. Rounds get dates;
  // games[] stays empty until tipoffs are posted.
  // Source: patriotleague.org/sports/wsoc.
  patriot: {
    2026: {
      rounds: {
        1: "Sun Nov 8",
        2: "Wed Nov 12",
        3: "Sun Nov 16",
      },
    },
  },

  // MAC Women's Soccer Championship 2026 — 6 teams; final at
  // ScottsMiracle-Gro Field, Columbus (home of the Crew). QF at
  // campus sites (top 2 seeds bye). All times published.
  // Source: getsomemaction.com/news/2026/10/2 + miamiredhawks.com.
  // build6 slots: R1 slot 1=4v5, slot 2=3v6. R2 slot 1=1 v w(1),
  // slot 2=2 v w(2). The two QFs share Nov 7 @ 5 PM ET.
  mac: {
    2026: {
      rounds: {
        1: "Sat Nov 7",
        2: "Tue Nov 10",
        3: "Fri Nov 13",
      },
      games: {
        "1-1": { date: "Sat Nov 7", time: "5:00 PM ET" }, // 4v5
        "1-2": { date: "Sat Nov 7", time: "5:00 PM ET" }, // 3v6
        "2-1": { date: "Tue Nov 10", time: "5:00 PM ET" },
        "2-2": { date: "Tue Nov 10", time: "5:00 PM ET" },
        "3-1": { date: "Fri Nov 13", time: "7:30 PM ET" },
      },
    },
  },

  // Big South Women's Soccer Championship 2026 — 6 teams; higher
  // seeds host every round. All times published.
  // Source: bigsouthsports.com/news/2026/11/1.
  // build6 slots: R1 slot 1=4v5 (Gardner-Webb at Radford), slot 2=3v6
  // (Longwood at High Point).
  big_south: {
    2026: {
      rounds: {
        1: "Thu Nov 5",
        2: "Tue Nov 10",
        3: "Sun Nov 15",
      },
      games: {
        "1-1": { date: "Thu Nov 5", time: "7:00 PM ET" }, // 4v5
        "1-2": { date: "Thu Nov 5", time: "7:00 PM ET" }, // 3v6
        "2-1": { date: "Tue Nov 10", time: "6:00 PM ET" },
        "2-2": { date: "Tue Nov 10", time: "6:00 PM ET" },
        "3-1": { date: "Sun Nov 15", time: "1:00 PM ET" },
      },
    },
  },

  // Pac-12 Women's Soccer Championship 2026 — 4 teams, hosted at
  // Chuck & Gloria Bell Field, Logan, UT (Utah State's venue). SF
  // and F times published.
  // Source: pac-12.com/news/2026/9/29 + utahstateaggies.com.
  // build4 slots: R1 slot 1=1v4, slot 2=2v3. Earlier SF time by
  // convention goes to slot 1.
  pac_12: {
    2026: {
      rounds: {
        1: "Wed Nov 11",
        2: "Sat Nov 14",
      },
      games: {
        "1-1": { date: "Wed Nov 11", time: "4:00 PM MT" }, // 1v4
        "1-2": { date: "Wed Nov 11", time: "7:30 PM MT" }, // 2v3
        "2-1": { date: "Sat Nov 14", time: "7:30 PM MT" },
      },
    },
  },

  // ASUN Women's Soccer Championship 2026 — 6 teams; top two seeds
  // host rounds 1-2 at Pickering Field, Fort Myers (FGCU). First-
  // round kickoff at 7 PM, semifinals at 1 PM, final Nov 13 at the
  // top seed's site. The source doesn't pin which specific first-
  // round matchup is at 7 PM vs its sibling, so game-level entries
  // stay off — round dates carry the signal.
  // Source: fgcuathletics.com/news/2026/10/29.
  asun: {
    2026: {
      rounds: {
        1: "Wed Nov 5",
        2: "Sat Nov 8",
        3: "Thu Nov 13",
      },
    },
  },

  // Ivy League Women's Soccer Tournament 2026 — 4 teams. Hosted at
  // the No. 1 seed's site; no kickoff times posted.
  // Source: ivyleague.com/sports/2026/7/23 (2026-27 sites + dates).
  ivy_league: {
    2026: {
      rounds: {
        1: "Thu Nov 12",
        2: "Sun Nov 15",
      },
    },
  },

  // WCC Women's Soccer Championship 2026 — 4 teams, inaugural year.
  // Highest seed hosts; no kickoff times posted (location was TBD
  // through the regular season).
  // Source: wccsports.com/sports/2026/7/7 + smcgaels.com/news/2025/6/9.
  wcc: {
    2026: {
      rounds: {
        1: "Tue Nov 11",
        2: "Fri Nov 14",
      },
    },
  },

  // Big West Women's Soccer Championship 2026 — 6 teams; higher seeds
  // host every round. No kickoff times posted ahead of the tournament.
  // Source: bigwest.org/tournaments/?id=153.
  big_west: {
    2026: {
      rounds: {
        1: "Wed Nov 4",
        2: "Sat Nov 7",
        3: "Sat Nov 14",
      },
    },
  },

  // Missouri Valley Conference Women's Soccer Championship 2026 — 6
  // teams; higher seeds host every round. Round dates published, no
  // kickoff times posted ahead of the tournament.
  // Source: mvc-sports.com/news/2026/8/19 (2026-27 championship schedule).
  mvc: {
    2026: {
      rounds: {
        1: "Sat Nov 8",
        2: "Wed Nov 12",
        3: "Sat Nov 15",
      },
    },
  },

  // America East Women's Soccer Championship 2026 — 6 teams; higher
  // seeds host every round via Championship Central. No kickoff times
  // posted. League lists the final date range as "Nov 14 / 15"; the
  // round key takes the earlier date and the final slot plays on
  // either day at highest-remaining-seed convenience.
  // Source: americaeast.com/sports/2026/7/21.
  america_east: {
    2026: {
      rounds: {
        1: "Wed Nov 5",
        2: "Sat Nov 8",
        3: "Fri Nov 14",
      },
    },
  },
};

// bracketScheduleFor returns the schedule overlay for (conference,
// year) or undefined when none has been wired. The conference slug is
// normalised to the underscore form conferences.yaml uses, so callers
// can pass either "big_ten" or "big-ten" without caring.
export function bracketScheduleFor(
  conference: string,
  year: number,
): BracketSchedule | undefined {
  const key = conference.replace(/-/g, "_");
  return SCHEDULES[key]?.[year];
}

// bracketSeasonYear picks the fall-sport season year from the
// current date. Soccer and football seasons open in August and the
// conference tournaments run in November, so any date from August
// onward belongs to that calendar year's tournament. January–July
// falls under the previous year's bracket (useful only for
// after-the-fact browsing — the projected bracket is reasonable then
// only in archival contexts).
export function bracketSeasonYear(now: Date = new Date()): number {
  // getMonth is 0-indexed; July = 6.
  return now.getMonth() >= 7 ? now.getFullYear() : now.getFullYear() - 1;
}
